import { useCallback, useRef } from "react";
import type { ReactNode } from "react";
import {useBoolean} from '@fluentui/react-hooks';
import {
    Calendar as FluentCalendar,
    Callout,
    DateRangeType,
    DirectionalHint,
    FocusTrapZone,
    PrimaryButton,
} from '@fluentui/react';
import { endOfDay, startOfDay } from "date-fns";

interface IProps {
    date: Date
    setDate: (date:Date)=>void
    dateRange: Date[]
    setDateRange: (date:Date[])=>void
    dateRangeType: DateRangeType
    setToCurrentWeek: () => void
    setToCurrentWeekButtonDisabled: boolean
    dateRender: ReactNode;
}

const Calendar = ({date, setDate: _setDate, dateRange, setDateRange, dateRangeType, setToCurrentWeek, setToCurrentWeekButtonDisabled, dateRender}: IProps)=> {
    const [showCalendar, {toggle: toggleCalendar, setFalse: hideCalendar}] = useBoolean(false)
    const buttonContainerRef = useRef<HTMLDivElement>(null);
    const highlightedStart = dateRange?.length >= 2 ? startOfDay(dateRange[0]) : null;
    const highlightedEnd = dateRange?.length >= 2 ? endOfDay(dateRange[1]) : null;

    const onSelectDate = (date: Date, dateRangeArray?: Date[]) => {
        // Do not push an immediate date write here; let setDateRange flow resolve payroll first.
        if (dateRangeArray) {
            setDateRange(dateRangeArray);
        } else {
            setDateRange([date]);
        }
        hideCalendar();
    }

    // Tag day cells that fall inside the payroll range so CSS can highlight them.
    const onCustomDayCellRef = useCallback((element: HTMLElement, dayDate: Date) => {
        if (!element) return;
        const isInPayrollRange = Boolean(
            highlightedStart &&
            highlightedEnd &&
            dayDate.getTime() >= highlightedStart.getTime() &&
            dayDate.getTime() <= highlightedEnd.getTime()
        );

        // Apply the marker class to both td and its button for consistent styling.
        element.classList.toggle('rb-payroll-range-day', isInPayrollRange);
        const buttonEl = element.querySelector('button');
        if (buttonEl) {
            buttonEl.classList.toggle('rb-payroll-range-day', isInPayrollRange);
        }
    }, [highlightedStart, highlightedEnd]);

    return (
        <div>
            <div onClick={toggleCalendar} ref={buttonContainerRef}>
                <div style={{ padding: '1rem 1.5rem' }} className={'calendar-text'}>
                    {dateRender}
                </div>
            </div>
            {showCalendar && (
                <Callout
                    isBeakVisible={false}
                    gapSpace={0}
                    doNotLayer={false}
                    target={buttonContainerRef}
                    directionalHint={DirectionalHint.bottomCenter}
                    onDismiss={hideCalendar}
                    setInitialFocus
                >
                    <FocusTrapZone isClickableOutsideFocusTrap>
                        <FluentCalendar
                            dateRangeType={dateRangeType}
                            onDismiss={hideCalendar}
                            isMonthPickerVisible
                            value={date}
                            highlightCurrentMonth
                            isDayPickerVisible
                            showGoToToday={false}
                            onSelectDate={onSelectDate}
                            calendarDayProps={{ customDayCellRef: onCustomDayCellRef }}
                        />
                        
                        <div style={{display: 'flex', justifyContent: 'flex-end', padding: '0 5px 5px 0'}}>
                            <PrimaryButton onClick={() => {
                                setToCurrentWeek();
                                hideCalendar();
                            }}
                                        disabled={setToCurrentWeekButtonDisabled}>
                                            This Week
                            </PrimaryButton>
                        </div>
                    </FocusTrapZone>
                </Callout>
            )}
        </div>
    )
}

export {Calendar}
